import React from 'react';
import { Button, Input, Form, Row, Col, Select, Typography } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import CreditCardNumber from 'components/Shared/CardUtils/CreditCardNumber';
import CreditCardCvc from 'components/Shared/CardUtils/CreditCardCvc';
import useCardForm from 'components/Shared/CardUtils/useCardForm';
import paymentHelper from 'pages/PaymentsSpa/helper';
import { SUPPORTED_COUNTRIES } from 'constants.js';
import useTranslation from 'utils/hooks/useTranslation';
import useIsFormValid from 'utils/hooks/useIsFormValid';

import styles from './style.module.scss';

const { Option } = Select;

const AddCardForm = ({ corporateClientId, onCancel, onSubmit, submitting, language }) => {
  const { translateText } = useTranslation();
  const [form] = Form.useForm();

  const {
    creditCardType,
    isZipCodeRequired,
    expiryMonths,
    handleCreditCardChange,
    handleCvcChange,
    handleCountryChange,
    handleSubmit,
    cardNumberRules,
    cardTypeRules,
    nameOnCardRules,
    expiryMonthRules,
    expiryYearRules,
    cvvRules,
    zipCodeRules,
    countryCodeRules,
  } = useCardForm({ form, corporateClientId, onSubmit });

  const fieldValidationConfig = [
    { name: 'cardNumber', required: true },
    { name: 'paymentCardType', required: true },
    { name: 'nameOnCard', required: true },
    { name: 'expiryMonth', required: true },
    { name: 'expiryYear', required: true },
    { name: 'cvv', required: true },
    { name: ['billingAddress', 'zipCode'], required: isZipCodeRequired },
    { name: 'countryCode', required: true },
  ];
  const { isFormValid, validateForm } = useIsFormValid(form, fieldValidationConfig);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const label = key => `${translateText(key)} *`;

  return (
    <Form
      form={form}
      layout="vertical"
      className={styles.compactAddCardForm}
      onFieldsChange={validateForm}
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Form.Item
            name="cardNumber"
            label={label('corporateAccounts.payments.cardNumber')}
            required={false}
            rules={cardNumberRules}
          >
            <CreditCardNumber
              placeholder={translateText('corporateAccounts.payments.form.cardNumberPlaceholder')}
              onChange={handleCreditCardChange}
              prefix={<CreditCardOutlined />}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Form.Item
            name="paymentCardType"
            label={label('corporateAccounts.payments.cardType')}
            required={false}
            rules={cardTypeRules}
          >
            <Input
              placeholder={translateText('corporateAccounts.payments.form.cardTypePlaceholder')}
              disabled
              readOnly
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="nameOnCard"
            label={label('corporateAccounts.payments.nameOnCard')}
            required={false}
            rules={nameOnCardRules}
          >
            <Input
              placeholder={translateText('corporateAccounts.payments.form.nameOnCardPlaceholder')}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Form.Item
            name="expiryMonth"
            validateTrigger="onBlur"
            label={label('corporateAccounts.payments.expiryMonth')}
            required={false}
            rules={expiryMonthRules}
          >
            <Select
              placeholder={translateText('corporateAccounts.payments.form.expiryMonthPlaceholder')}
            >
              {expiryMonths.map(month => (
                <Option
                  data-testid={`selectExpiryMonth-${month.label}`}
                  key={month.value}
                  value={month.value}
                >
                  {month.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="expiryYear"
            label={label('corporateAccounts.payments.expiryYear')}
            required={false}
            rules={expiryYearRules}
          >
            <Input
              placeholder={translateText('corporateAccounts.payments.form.expiryYearPlaceholder')}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Form.Item
            name="cvv"
            label={label('corporateAccounts.payments.cardCvc')}
            required={false}
            rules={cvvRules}
          >
            <CreditCardCvc
              onChange={handleCvcChange}
              cardType={creditCardType}
              placeholder={translateText('corporateAccounts.payments.cvc')}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['billingAddress', 'zipCode']}
            label={label('corporateAccounts.payments.zipCode')}
            required={false}
            rules={zipCodeRules}
          >
            <Input
              placeholder={translateText('corporateAccounts.payments.form.zipCodePlaceholder')}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col span={24}>
          <Form.Item
            name="countryCode"
            validateTrigger="onBlur"
            label={label('corporateAccounts.payments.form.primaryCountryOfUse')}
            required={false}
            rules={countryCodeRules}
          >
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              placeholder={translateText(
                'corporateAccounts.payments.form.cardCountryCodePlaceholder',
              )}
              onChange={handleCountryChange}
            >
              {Object.keys(SUPPORTED_COUNTRIES).map(key => (
                <Option
                  key={SUPPORTED_COUNTRIES[key].countryCode}
                  value={SUPPORTED_COUNTRIES[key].countryCode}
                  data-testid={`selectCountry-${SUPPORTED_COUNTRIES[key].countryCode}`}
                >
                  {translateText(
                    `corporateAccounts.countries.${SUPPORTED_COUNTRIES[
                      key
                    ].countryCode.toLowerCase()}`,
                    { defaultMessage: SUPPORTED_COUNTRIES[key].label },
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24} className={styles.compactAddCardTerms}>
          {translateText('corporateAccounts.payments.form.termsAgreement')}
          &nbsp;
          <Typography.Link
            href={paymentHelper.getStoredCredentialAgreementUrl(language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translateText('corporateAccounts.payments.form.storedCredentialAgreement')}
          </Typography.Link>
          .
        </Col>
      </Row>
      <Row className={styles.compactAddCardFooter}>
        <Col>
          <Button onClick={handleCancel}>
            {translateText('corporateAccounts.actions.cancel')}
          </Button>
          <Button
            data-testid="submitSpaPaymentForm"
            loading={submitting}
            onClick={handleSubmit}
            type="primary"
            disabled={!isFormValid}
          >
            {translateText('corporateAccounts.payments.actions.addAndAgree')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default AddCardForm;
